import React from 'react';
import PropTypes from 'prop-types';
import { uniq } from 'lodash';
import moment from 'moment';
import { Panel, PanelType, IconButton } from 'office-ui-fabric-react';
import { useBoolean } from '@uifabric/react-hooks';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { Text } from 'office-ui-fabric-react/lib/Text';
import Link from '../Link';
import DetailsList from '../DetailsList';
import styles from './FilterDetailsList.style';
import { formatDateString, removeEmptyProperties, convertCamelCase } from '../../utils';
import TextButton from '../TextButton';
import FluentGrid from '../../components/FluentGrid';
import {
  computedValueTypes,
  dimensionListTypes,
  getCalcParamsText,
  operatorMap,
  customerTier,
  dlColumnKeys,
} from '../../constants';
import { calculationMethods } from '../CalculationFeatures/constants';
import SectionHeader from '../../components/SectionHeader';
import saveExcel from '../../utils/excel';
import { recoupmentPercentColumnAsListItem } from '../DynamicDetailsList/recoupmentColumns';
import { allColumns, unitOfMeasureColumn } from '../DynamicDetailsList/ContractTransactionColumns';
import { expandRateAssignments } from '../RoyaltyFeeForm/RoyaltyFeeForm';

const isTiered = agreement => !!agreement.tieringMethod;

const dimensionMap = allColumns.reduce((acc, column) =>
  column.key && column.name ? { ...acc, [column.key]: column.name } : acc,
);

export const getAgreementAndCalcParamHeader = agreement => () => {
  return (
    <FluentGrid className={styles.nameSection}>
      <FluentGrid row>
        <FluentGrid col sm={6} md={3}>
          <SectionHeader noBorder noSpacing>
            Agreement Name
          </SectionHeader>
          <Text as="p" variant="small">
            {agreement.name}
          </Text>
        </FluentGrid>
        <FluentGrid col sm={6} md={3}>
          <SectionHeader noBorder noSpacing>
            Calculation Method
          </SectionHeader>
          <Text as="p" variant="small">
            {agreement.calculation.name}
          </Text>
        </FluentGrid>
        {isTiered(agreement) && (
          <>
            <FluentGrid col sm={6} md={3}>
              <SectionHeader noBorder noSpacing>
                Tiering Method
              </SectionHeader>
              <Text as="p" variant="small">
                {agreement.tieringMethodFriendly || agreement.tieringMethod}
              </Text>
            </FluentGrid>
            <FluentGrid col sm={6} md={3}>
              <SectionHeader noBorder noSpacing>
                Tiering Type
              </SectionHeader>
              <Text as="p" variant="small">
                {agreement.tieringTypeFriendly || agreement.tieringType}
              </Text>
            </FluentGrid>
            <FluentGrid col sm={6} md={3}>
              <SectionHeader noBorder noSpacing>
                Tiering Reset Interval
              </SectionHeader>
              <Text as="p" variant="small">
                {agreement.tieringResetIntervalFriendly || agreement.tieringResetInterval}
              </Text>
            </FluentGrid>
            {agreement.tieringResetInterval !== 'contractToDate' && (
              <FluentGrid col sm={6} md={3}>
                <SectionHeader noBorder noSpacing>
                  Tiering Interval Start Date
                </SectionHeader>
                <Text as="p" variant="small">
                  {moment.utc(agreement.tieringResetStartDate).format('YYYY-MM-DD')}
                </Text>
              </FluentGrid>
            )}
          </>
        )}
      </FluentGrid>
    </FluentGrid>
  );
};

export const getCalculationFeatureHeader = feature => () => {
  const calcMethod = calculationMethods[feature.calculationMethod];
  return (
    <FluentGrid className={styles.nameSection}>
      <FluentGrid row>
        <FluentGrid col sm={6} md={3}>
          <SectionHeader noBorder noSpacing>
            Calculation Method
          </SectionHeader>
          <Text as="p" variant="small">
            {calcMethod?.text}
          </Text>
        </FluentGrid>
      </FluentGrid>
    </FluentGrid>
  );
};

// returns items for cell in excel
const itemsForCell = (items, prepend) => {
  const itemsInCell = [];
  const columnsHeader = [];
  items.forEach(item => {
    const itemObj = prepend(item);
    itemObj.product = item.product?.displayName || '';
    itemObj.geography = item.geography?.displayName || '';
    itemObj.currencyCode = item.currency?.displayName || '';
    itemObj.effectiveDate = formatDateString(item.effectiveDate);
    itemObj.terminationDate = formatDateString(item.terminationDate);
    itemsInCell.push(removeEmptyProperties(itemObj));
    Object.keys(itemObj).forEach(column => columnsHeader.push(convertCamelCase(column)));
  });
  return { itemsInCell, columnsHeader: uniq(columnsHeader) };
};

export const mapFilterRows = (filters, dimensions) =>
  expandRateAssignments(filters).map(filter => {
    const filterRow = {
      ...filter,
      ...dimensions.reduce((agg, d) => ({ ...agg, [d]: { displayName: 'any' } }), {}),
    };

    if (filter?.dimensions) {
      // Adding each property inside dimensions array to filter attribute, so that detailList can get the value from column's fieldName
      dimensions.forEach(dimension => {
        if (filter && filter.dimensions?.length > 0) {
          filter.dimensions.forEach(filterDimension => {
            if (filterDimension && filterDimension.property === dimension) {
              filterRow[dimension] = {
                displayName: filterDimension.displayName
                  ? filterDimension.displayName
                  : filterDimension.value,
                propertyType: filterDimension.propertyType,
                propertyLink:
                  filterDimension.propertyType === 'dimensionList'
                    ? `/dimensionLists/${filterDimension.value}/${filterDimension.property}`
                    : filterDimension.property === 'product' &&
                      `/products/${filterDimension.value}`,
              };
            }
          });
        }

        if (!filterRow[dimension]) {
          filterRow[dimension] = {
            displayName: 'Any',
            propertyType: 'value',
          };
        }
      });

      // For Agreement filters:
      // Adding each property inside calcParams array  to filter attribute, so that detailList can get the value from column's fieldName
      if (filter && filter.calcParams?.length > 0) {
        filter.calcParams.forEach(calcParam => {
          filterRow[calcParam.property] = calcParam.value;
        });
      }
      return filterRow;
    }
    return null;
  });

export const filterPrepend = dimension => ({ type: dimension.type });
export const feePrepend = dimension => ({
  tier: dimension.tierId ? parseInt(dimension.tierId, 10) : undefined,
  unitRate: dimension.calcParams[0]?.property === 'unitRate' ? dimension.calcParams[0]?.value : '',
});
export const featurePrepend = assignment => assignment.calcParams;
export const groupAssignmentsPrepend = assignment => ({
  'Recoup%':
    assignment.recoupmentParams[0]?.property === 'recoupmentPercentage'
      ? assignment.recoupmentParams[0]?.value
      : '',
});
export const feeHeader = agreement => {
  const cell = (location, value) => ({ cell: location, value });
  const headerCell = (location, value) => ({
    ...cell(location, value),
    font: { bold: true, size: 16 },
    alignment: { horizontal: 'right' },
  });
  const mainHeader = [
    headerCell('A1', 'Agreement Name'),
    headerCell('B1', 'Calculation Method'),
    cell('A2', agreement.name),
    cell('B2', agreement.calculation.name),
  ];
  const getTierHeader = () => {
    const tierReset =
      agreement.tieringResetInterval === 'contractToDate'
        ? []
        : [
            headerCell('F1', 'Tiering Reset Start Date'),
            cell('F2', agreement.tieringResetStartDate),
          ];
    return [
      headerCell('C1', 'Tiering Method'),
      cell('C2', agreement.tieringMethod),
      headerCell('D1', 'Tiering Type'),
      cell('D2', agreement.tieringType),
      headerCell('E1', 'Tiering Reset Interval'),
      cell('E2', agreement.tieringResetInterval),
      ...tierReset,
    ];
  };
  return isTiered(agreement) ? [...mainHeader, ...getTierHeader()] : mainHeader;
};
export const featureHeader = feature => {
  const calcMethod = calculationMethods[feature.calculationMethod];
  return [
    {
      cell: 'A1',
      value: 'Calculation Method',
      font: { bold: true, size: 16 },
      alignment: { horizontal: 'right' },
    },
    {
      cell: 'A2',
      value: calcMethod?.text,
    },
    {
      cell: 'B1', // this is required due to the architecture of the excel export
    },
    {
      cell: 'B2',
    },
  ];
};

export const exportToExcel = (filter, header, prepend) => fileName => e => {
  e.stopPropagation();

  const { itemsInCell, columnsHeader } = filter && itemsForCell(filter, prepend);

  saveExcel(columnsHeader, itemsInCell, header, {
    fileName,
    workSheetName: fileName,
  });
};

export const exportFilterToExcel = filter =>
  exportToExcel(mapFilterRows(filter.filters, filter.dimensions), [], filterPrepend);

export const exportFeeToExcel = agreement => {
  return exportToExcel(
    mapFilterRows(agreement.rateAssignments, agreement.dimensions),
    feeHeader(agreement),
    feePrepend,
  );
};

export const exportFeatureToExcel = feature =>
  exportToExcel(
    mapFilterRows(feature.calculations, feature.dimensions),
    featureHeader(feature),
    featurePrepend,
  );

export const exportGroupAssignmentsToExcel = groupAssignments => {
  return exportToExcel(
    mapFilterRows(groupAssignments.filters, groupAssignments.dimensions),
    [],
    groupAssignmentsPrepend,
  );
};

const FilterDetailsList = ({
  dimensions,
  filters,
  header,
  onRenderHeader,
  onExport,
  calcParams,
  isGroupAssignmentFilter,
  buttonText,
}) => {
  if (!filters || filters.length === 0 || !dimensions || dimensions.length === 0) {
    return <></>;
  }
  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);

  const getPropertyText = property => {
    const dimensionListType = dimensionListTypes.find(e => e.key === property);

    if (dimensionListType) {
      return dimensionListType.text;
    }

    const computedValueType = computedValueTypes.find(e => e.key === property);

    if (computedValueType) {
      return computedValueType.text;
    }

    const dimensionColumn = dimensionMap[property];

    if (dimensionColumn) {
      return dimensionColumn;
    }

    return property;
  };

  const dimensionColumns = dimensions.map(dimension => {
    return {
      key: dimension,
      name: getPropertyText(dimension),
      fieldName: dimension,
      isResizable: false,
      minWidth: 200,
      maxWidth: 200,
      onRender: filter => {
        if (filter[dimension] && filter[dimension].propertyType === 'dimensionList') {
          return (
            <>
              <FontIcon
                iconName="BulletedList"
                arianame="BulletedList"
                className={styles.bulletedList}
              />{' '}
              {filter[dimension]?.propertyLink && (
                <Link
                  className={styles.filterDimensionLink}
                  href={filter[dimension].propertyLink}
                  target="_blank"
                >
                  {filter[dimension]?.operator && (
                    <span>{operatorMap.get(filter[dimension].operator)}</span>
                  )}
                  <strong> {filter[dimension].displayName}</strong>
                </Link>
              )}
              {!filter[dimension]?.propertyLink && (
                <>
                  {filter[dimension]?.operator && (
                    <span>{operatorMap.get(filter[dimension].operator)}</span>
                  )}
                  <strong> {filter[dimension].displayName}</strong>
                </>
              )}
            </>
          );
        }
        return filter[dimension]?.propertyLink ? (
          <Link
            className={styles.filterDimensionLink}
            href={filter[dimension].propertyLink}
            target="_blank"
          >
            {filter[dimension]?.operator && (
              <span>{operatorMap.get(filter[dimension].operator)}</span>
            )}
            <strong> {filter[dimension].displayName}</strong>
          </Link>
        ) : (
          <>
            {filter[dimension]?.operator && (
              <span>{operatorMap.get(filter[dimension].operator)}&nbsp;</span>
            )}
            <span>{filter[dimension].displayName}</span>
          </>
        );
      },
    };
  });

  const filterRows = expandRateAssignments(filters).map(filter => {
    const filterRow = {
      ...filter,
      ...dimensions.reduce((agg, d) => ({ ...agg, [d]: { displayName: 'any' } }), {}),
    };
    if (filter?.dimensions) {
      // Adding each property inside dimensions array to filter attribute, so that detailList can get the value from column's fieldName
      dimensions.forEach(dimension => {
        if (filter && filter.dimensions?.length > 0) {
          filter.dimensions.forEach(filterDimension => {
            if (filterDimension && filterDimension.property === dimension) {
              filterRow[dimension] = {
                displayName: filterDimension.displayName
                  ? filterDimension.displayName
                  : filterDimension.value,
                operator: filterDimension.operator,
                propertyType: filterDimension.propertyType,
                propertyLink:
                  filterDimension.propertyType === 'dimensionList'
                    ? `/dimensionLists/${filterDimension.value}/${filterDimension.property}`
                    : filterDimension.property === 'product' &&
                      `/products/${filterDimension.value}`,
              };
              if (dimension === dlColumnKeys.customerTier) {
                const propertyValue = customerTier.find(e => e.key === filterDimension.value);
                filterRow[dimension] = {
                  displayName: propertyValue?.text,
                };
              }
            }
          });
        }

        // unit of measure is a property on the rate assignment, not an actual filter
        // so it requires special treatment here
        if (dimension === 'unitOfMeasure') {
          const foundOption = unitOfMeasureColumn.props.options.find(
            option => option.key === filter.unitOfMeasure,
          );

          filterRow[dimension] = {
            displayName: foundOption?.text || filter.unitOfMeasure,
            propertyType: 'value',
          };
        }

        if (dimension === 'cancellationDaysSincePurchased') {
          const foundOption = computedValueTypes.find(
            option =>
              option.key ===
              `${filterRow[dimension].operator}${filterRow[dimension].displayName}Days`,
          );

          filterRow[dimension] = {
            displayName: foundOption?.text || filter.value,
            propertyType: 'value',
          };
        }

        if (!filterRow[dimension]) {
          filterRow[dimension] = {
            displayName: 'Any',
            propertyType: 'value',
          };
        }
      });

      // For Agreement filters:
      // Adding each property inside calcParams array  to filter attribute, so that detailList can get the value from column's fieldName
      if (filter && filter.calcParams?.length > 0) {
        filter.calcParams.forEach(calcParam => {
          filterRow[calcParam.property] = calcParam.value;
        });
      }

      // for Group assignment (recoupment) filters
      if (filter && filter.recoupmentParams?.length > 0) {
        filter.recoupmentParams.forEach(recoupParam => {
          filterRow[recoupParam.property] = recoupParam.value;
        });
      }
    }
    return filterRow;
  });

  let prefixColumns = [
    {
      key: 'exclude',
      name: 'Exclude',
      maxWidth: 50,
      minWidth: 50,
      onRender: filter => {
        const iconName = filter.type === 'exclude' ? 'CompletedSolid' : '';
        return (
          <IconButton
            className={styles.excludeButton}
            ariaLabel="select"
            iconProps={{ iconName }}
          />
        );
      },
    },
  ];

  if (calcParams && calcParams.length > 0) {
    prefixColumns = [];
    calcParams.forEach(calcProp => {
      prefixColumns.push({
        key: calcProp,
        name: getCalcParamsText(calcProp),
        maxWidth: 100,
        minWidth: 100,
        fieldName: calcProp,
      });
    });
  }

  if (filterRows?.length && filterRows[0].tierId) {
    prefixColumns = [
      {
        key: 'tierId',
        name: 'Tier',
        maxWidth: 100,
        minWidth: 100,
        fieldName: 'tierId',
      },
      {
        key: 'tieringUpper',
        name: 'Threshold',
        maxWidth: 100,
        minWidth: 100,
        fieldName: 'tieringUpper',
      },
      ...prefixColumns,
    ];
  }

  if (isGroupAssignmentFilter) {
    prefixColumns = [recoupmentPercentColumnAsListItem()];
  }

  const columns = [
    ...prefixColumns,
    ...dimensionColumns,
    {
      key: 'effectiveDate',
      name: 'Effective Date',
      fieldName: 'effectiveDate',
      isResizable: false,
      minWidth: 100,
      maxWidth: 100,
      onRender: filter => (filter.effectiveDate ? `${formatDateString(filter.effectiveDate)}` : ''),
    },
    {
      key: 'terminationDate',
      name: 'Expiration Date',
      fieldName: 'terminationDate',
      isResizable: false,
      minWidth: 100,
      maxWidth: 100,
      onRender: filter =>
        filter.terminationDate ? `${formatDateString(filter.terminationDate)}` : '',
    },
  ];

  return (
    <div className={styles.buttonContainer}>
      <TextButton className={styles.viewDetails} onClick={openPanel}>
        {buttonText}
      </TextButton>
      <Panel
        isOpen={isOpen}
        onDismiss={dismissPanel}
        type={PanelType.smallFluid}
        closeButtonAriaLabel="Close"
        headerText={header}
        className={styles.panelHeader}
      >
        {onRenderHeader && onRenderHeader()}

        <div className={styles.buttonContainer}>
          <TextButton className={styles.exportToExcel} onClick={onExport(header)}>
            <FontIcon
              iconName="DownloadDocument"
              arianame="DownloadDocument"
              className={styles.bulletedList}
            />
            Export to Excel
          </TextButton>
        </div>

        {filterRows?.length > 0 && (
          <div data-automationid="scope-filters-list">
            <DetailsList
              items={filterRows.map(filter => ({
                ...filter,
                key: filter._id,
                dataAutomationId: `row-${filter._id}`,
              }))}
              columns={columns}
            />
          </div>
        )}
      </Panel>
    </div>
  );
};

FilterDetailsList.propTypes = {
  dimensions: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      effectiveDate: PropTypes.string,
      terminationDate: PropTypes.string,
      dimensions: PropTypes.arrayOf(
        PropTypes.shape({
          property: PropTypes.string,
          value: PropTypes.string,
          operator: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  calcParams: PropTypes.arrayOf(PropTypes.string),
  isGroupAssignmentFilter: PropTypes.bool,
  onRenderHeader: PropTypes.func,
  header: PropTypes.string.isRequired,
  onExport: PropTypes.func,
  buttonText: PropTypes.string.isRequired,
};

FilterDetailsList.defaultProps = {
  calcParams: [],
  isGroupAssignmentFilter: false,
  onRenderHeader: null,
  onExport: () => {},
};

export default FilterDetailsList;
